import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Button, Alert } from 'rsuite';
import { Avatar, TextField, Typography, Box } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import loginService from '../../../services/login';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

const hasLength = new RegExp('^(?=.{8,})');
const hasLower = new RegExp('^(?=.*[a-z])');
const hasUpper = new RegExp('^(?=.*[A-Z])');
const hasNum = new RegExp('^(?=.*[0-9])');
const hasSpecial = new RegExp('^(?=.*[-+!@#$%^&*])');

export default function Verify () {
    const history = useHistory();
    const query = new URLSearchParams(useLocation().search);
    const token = query.get('token');
    const idToken = Number(query.get('_'));

    const [isLoading, setIsLoading] = useState(false);
    const [validateUserId, setValidateUserId] = useState();

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };
    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const validations = useMemo(() => {
        return {
            hasLength: hasLength.test(password),
            hasLower: hasLower.test(password),
            hasUpper: hasUpper.test(password),
            hasNum: hasNum.test(password),
            hasSpecial: hasSpecial.test(password),
            equals: password === passwordConfirm,
        };
    }, [password, passwordConfirm]);

    const validateUser = async () => {
        try {
            const response = await loginService.validateForgot({
                idPlatform: 5,
                metadata: {
                    token,
                },
                idToken
            });

            setValidateUserId(response.id);

            Alert.success('El usuario fue validado correctamente. Por Favor, ingrese su nueva contraseña.', 5000);
        }
        catch (error) {
            if (error.response?.data?.extra?.key?.startsWith('Not Found OTP') || [404, 401].includes(error.response.status)) {
                Alert.warning('Lo sentimos, el token venció. Para recuperar su contraseña, vuelva a ingresar su mail', 5000);
                history.push('/auth/forgot');
                return;
            }
            Alert.error('Se produjo un error interno, te pedimos disculpas. Por favor, intentalo de nuevo más tarde.', 5000);
        }
    };

    const changePassword = async (event) => {
        try {
            event.preventDefault();
            setIsLoading(true);

            await loginService.change({
                idPlatform: 5,
                idUser: validateUserId,
                idToken, // otp
                token,
                password
            });

            setIsLoading(false);
            Alert.success('Su nueva contraseña fue creada exitosamente.', 5000);
            history.push('/auth/login');
        }
        catch (error) {
            const response = error.response.data;

            if (response.code === 400 && response.extra.key === 'password_already_used') {
                Alert.warning('Lo sentimos, no puede utilizarse la misma contraseña dos veces.');
                setIsLoading(false);
                return;
            }

            if (response.code === 400 && response.extra.invalidations && response.extra.invalidations.includes('personal_data')) {
                Alert.warning('La contraseña no debe contener datos personales.');
                setIsLoading(false);
                return;
            }

            if (response.code === 400) {
                Alert.warning('Lo sentimos, la contraseña ingresada no es valida.');
                setIsLoading(false);
                return;
            }

            Alert.error('Se produjo un error interno, te pedimos disculpas. Por favor, intentalo de nuevo más tarde.', 5000);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        validateUser();
        // eslint-disable-next-line
    }, []);

    return (
        <div className='auth-page'>
            <Avatar>
                <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
                Recuperar Contraseña
            </Typography>
            <form noValidate onSubmit={changePassword}>
                <TextField
                    id="password"
                    label="Nueva Contraseña"
                    name="password"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    autoFocus fullWidth
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    id="password-confirm"
                    label="Confirme Contraseña"
                    name="password-confirm"
                    variant="outlined"
                    type={showConfirmPassword ? 'text' : 'password'}
                    fullWidth
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowConfirmPassword}
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Box display='flex' gap='17px' >
                    <Box>
                        <Typography>La contraseña debe tener al menos: </Typography>
                    </Box>
                    <Box display='flex' flexDirection='column' gap='18px'>
                        <Box display='flex'>
                            <CheckCircleIcon fontSize='small' color={validations.hasLength ? 'primary' : 'disabled'} />
                            <Typography>8 caracteres.</Typography></Box>
                        <Box display='flex'>
                            <CheckCircleIcon fontSize='small' color={validations.hasLower ? 'primary' : 'disabled'} />
                            <Typography>Un carácter en minúscula.</Typography></Box>
                        <Box display='flex'>
                            <CheckCircleIcon fontSize='small' color={validations.hasUpper ? 'primary' : 'disabled'} />
                            <Typography>Un carácter en mayúscula.</Typography></Box>
                        <Box display='flex'>
                            <CheckCircleIcon fontSize='small' color={validations.hasNum ? 'primary' : 'disabled'} />
                            <Typography>Un número.</Typography></Box>
                        <Box display='flex'>
                            <CheckCircleIcon fontSize='small' color={validations.hasSpecial ? 'primary' : 'disabled'} />
                            <Typography>Un carácter especial.</Typography></Box>
                    </Box>
                </Box>
                <Box>
                    <Typography style={{ color: 'red' }}>La contraseña no puede contener datos personales.</Typography>
                </Box>
                <Button
                    type="submit"
                    appearance='primary'
                    block
                    disabled={(
                        isLoading ||
                        !validations.hasLength ||
                        !validations.hasLower ||
                        !validations.hasUpper ||
                        !validations.hasNum ||
                        !validations.hasSpecial ||
                        !validations.equals
                    )}>
                    Aceptar
                </Button>
            </form>
        </div>
    );
};
